import { computed, inject, Injectable, signal } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatSnackBar } from '@angular/material/snack-bar';

import { SnackbarMode } from '@eiq/ui';

import { AddCondition, BlockModel, BooleanOperators, Condition, NumberOperators, Operator, QuestionType } from '@empathy/survey/base';
import { Question } from '@empathy/survey-core';

import { AffectedConditionsByMove } from '../models/conditions/affected-conditionals-by-move.model';
import { ConditionForm, IConditionForm } from '../models/conditions/condition-form.model';
import { RemoveConditionalsRequest } from '../models/conditions/remove-conditionals-request.model';
import { BlockTypeContainers } from '../models/survey-editor/block-type.constants';
import { SurveyEditor } from '../models/survey-editor/survey-editor.model';

@Injectable({
  providedIn: 'root'
})
export class SurveyConditionalsService {
  canApplyCondition$ = computed(() => this.canApplyCondition());
  conditionForm$ = signal<FormGroup<IConditionForm>>(new ConditionForm().form);
  questionSelected$ = signal<Question>(null);
  conditionFormValues$ = signal(this.conditionForm$().value);
  options$ = computed(() => this.questionSelected$().choices);

  private surveyEditor: SurveyEditor;

  private snackBar = inject(MatSnackBar);

  get conditionals(): { [qId: string]: Condition[] } {
    return {};
  }

  init(survey: SurveyEditor): void {
    this.surveyEditor = survey;

    this.surveyEditor.blocks.change$.subscribe((data) => this.handleGroupQuestionsChange(data));
  }

  addConditionToActiveQuestion(addCondition: AddCondition): void {
    const conditionToAdd = new ConditionForm(addCondition.condition).form;
    if (this.surveyEditor.activeQuestionConditionals.length > 0) {
      this.surveyEditor.activeQuestionConditionals.push(new FormControl(addCondition.command));
    }
    this.surveyEditor.activeQuestionConditionals.push(conditionToAdd);
  }

  addConditionToActiveQuestionOption(addCondition: AddCondition): void {
    const conditionToAdd = new ConditionForm(addCondition.condition).form;
    if (this.surveyEditor.activeQuestionOptionConditionals.length > 0) {
      this.surveyEditor.activeQuestionOptionConditionals.push(new FormControl(addCondition.command));
    }
    this.surveyEditor.activeQuestionOptionConditionals.push(conditionToAdd);
  }

  addConditionToActiveQuestionCard(addCondition: AddCondition): void {
    const conditionToAdd = new ConditionForm(addCondition.condition).form;
    if (this.surveyEditor.activeQuestionCardConditionals.length > 0) {
      this.surveyEditor.activeQuestionCardConditionals.push(new FormControl(addCondition.command));
    }
    this.surveyEditor.activeQuestionCardConditionals.push(conditionToAdd);
  }

  addConditionToActiveHiddenQuestionOption(addCondition: AddCondition): void {
    const conditionToAdd = new ConditionForm(addCondition.condition).form;
    if (this.surveyEditor.activeHiddenQuestionOptionConditionals.length > 0) {
      this.surveyEditor.activeHiddenQuestionOptionConditionals.push(new FormControl(addCondition.command));
    }
    this.surveyEditor.activeHiddenQuestionOptionConditionals.push(conditionToAdd);
  }

  affectedQuestionConditionsByOptionRemoved(optionRemovedId: string): string[] {
    return this.surveyEditor.affectedQuestionsOnConditionByOptionId(this.surveyEditor.activeQuestion.get('id').value, optionRemovedId);
  }

  affectedHiddenQuestionConditionsByOptionRemoved(optionRemovedId: string): string[] {
    return this.surveyEditor.affectedHiddenQuestionsOnConditionByHiddenOptionId(this.surveyEditor.activeQuestion.get('id').value, optionRemovedId);
  }

  affectedQuestionConditionsByCardRemoved(cardRemovedId: string): string[] {
    return this.surveyEditor.affectedQuestionsOnConditionByCardId(this.surveyEditor.activeQuestion.get('id').value, cardRemovedId);
  }

  affectedHiddenQuestionConditionsByCardRemoved(cardRemovedId: string): string[] {
    return this.surveyEditor.affectedHiddenQuestionsOnConditionByCardId(this.surveyEditor.activeQuestion.get('id').value, cardRemovedId);
  }

  affectedQuestionConditionsByMove(previousIndex: number, currentIndex: number): AffectedConditionsByMove {
    const qMovingId = this.surveyEditor.questionsCtrls.controls[previousIndex].get('id').value;
    const affectedQuestionsOnCondition = this.surveyEditor.affectedQuestionsOnConditionById(qMovingId);

    const response = new AffectedConditionsByMove();

    affectedQuestionsOnCondition.forEach((affectedQuestionId) => {
      const questionIndex = this.surveyEditor.getQuestionIndex(affectedQuestionId);
      if (questionIndex <= currentIndex) {
        response.other.push(affectedQuestionId);
      }
    });

    const affectedConditionalsOnQuestions = this.affectedConditionalsOnQuestions(previousIndex);

    affectedConditionalsOnQuestions.forEach((affectedQuestionId) => {
      const questionIndex = this.surveyEditor.getQuestionIndex(affectedQuestionId);
      if (questionIndex >= currentIndex) {
        response.current.push(affectedQuestionId);
      }
    });

    response.blocks = this.affectedBlockConditionalsByMove(qMovingId, currentIndex);

    return response;
  }

  onFormMultiOptionChanged(change: MatCheckboxChange, optionId: string): void {
    let valueToMatch = this.conditionFormValues$().valueToMatch ?? [];
    if (change.checked) {
      valueToMatch = [...valueToMatch, optionId];
    } else {
      valueToMatch = valueToMatch.filter((value) => value !== optionId);
    }

    this.conditionForm$().get('valueToMatch').setValue(valueToMatch);
  }

  onUpdateNumberOperatorValues(): void {
    const rawValueToMatch = this.conditionForm$().get('valueToMatch').value as unknown;
    const valueToMatch = Array.isArray(rawValueToMatch) ? rawValueToMatch[0] : rawValueToMatch;

    if (valueToMatch === null || valueToMatch === undefined || valueToMatch === '') {
      return;
    }

    this.conditionForm$()
      .get('valueToMatch')
      .setValue([String(valueToMatch)]);
  }

  onUpdateTextOperatorValues(): void {
    const valueToMatch = this.conditionForm$().get('valueToMatch').value;
    const formatedValue = Array.isArray(valueToMatch) ? valueToMatch[0] : (valueToMatch as unknown as string);

    this.conditionForm$()
      .get('valueToMatch')
      .setValue(formatedValue.split(';').filter((v) => !!v));
  }

  onUpdateSortRankNumericValues(): void {
    const valueToMatch = this.conditionForm$().get('valueToMatch').value;
    const formatedValue = Array.isArray(valueToMatch) ? valueToMatch.map((v) => v.split('_')[1]) : [(valueToMatch as unknown as string).split('_')[1]];

    this.conditionForm$().get('valueToMatch').setValue(formatedValue);
  }

  removeAllAffectedConditionsByMove(questionMovedId: string, affectedConditions: AffectedConditionsByMove): void {
    if (affectedConditions.current.length > 0) {
      this.removeConditions(new RemoveConditionalsRequest(questionMovedId, affectedConditions.current));
    }

    if (affectedConditions.other.length > 0) {
      affectedConditions.other.forEach((qId) => {
        this.removeConditions(new RemoveConditionalsRequest(qId, [questionMovedId]));
      });
    }

    if (affectedConditions.blocks.length > 0) {
      affectedConditions.blocks.forEach((blockAffected) => {
        const visibleWhen = this.surveyEditor.blocks.all.controls.find((block) => block.get('id').value === blockAffected.blockId).controls.visibleWhen;
        this.removeConditionsByQuestionId(visibleWhen, blockAffected.questionId);
      });
    }
  }

  removeConditionFromActiveQuestion(index: number): void {
    this.removeConditionFromArray(this.surveyEditor.activeQuestionConditionals, index);
  }

  removeConditionFromActiveOption(index: number): void {
    this.removeConditionFromArray(this.surveyEditor.activeQuestionOptionConditionals, index);
  }

  removeConditionFromActiveHiddenOption(index: number): void {
    this.removeConditionFromArray(this.surveyEditor.activeHiddenQuestionOptionConditionals, index);
  }

  removeConditionFromActiveCard(index: number): void {
    this.removeConditionFromArray(this.surveyEditor.activeQuestionCardConditionals, index);
  }

  removeConditionFromActiveBlock(index: number): void {
    this.removeConditionFromArray(this.surveyEditor.blocks.active$().get('visibleWhen') as FormArray, index);
  }

  removeConditions(request: RemoveConditionalsRequest): void {
    const questionCtrl = this.surveyEditor.questionsCtrls.controls.find((q: FormGroup) => q.get('id').value === request.questionId);

    this.findConditionIndexAndRemove(questionCtrl.get('visibleWhen') as FormArray, request.questionIdsToRemoveOnConditions, request.optionId, request.cardId);

    (questionCtrl.get('options') as FormArray)?.controls.forEach((opt) =>
      this.findConditionIndexAndRemove(opt.get('visibleWhen') as FormArray, request.questionIdsToRemoveOnConditions, request.optionId, request.cardId)
    );

    (questionCtrl.get('cards') as FormArray)?.controls.forEach((card) =>
      this.findConditionIndexAndRemove(card.get('visibleWhen') as FormArray, request.questionIdsToRemoveOnConditions, request.optionId, request.cardId)
    );
  }

  removeHiddenConditions(request: RemoveConditionalsRequest): void {
    request.questionIdsToRemoveOnConditions.forEach((hiddenQuestionId) => {
      const questionHiddenCtrl = this.surveyEditor.questionsHiddenCtrls.controls.find((q: FormGroup) => q.get('id').value === hiddenQuestionId);

      (questionHiddenCtrl.get('options') as FormArray)?.controls.forEach((opt) => {
        this.findConditionIndexAndRemove(opt.get('selectedWhen') as FormArray, [request.questionId], request.optionId, request.cardId);
      });
    });
  }

  removeAffectedConditions(): void {
    const activeQuestionId = this.surveyEditor.activeQuestion.get('id').value;
    this.removeAffectedConditionsById(activeQuestionId);
  }

  removeAffectedConditionsById(triggerQId: string): void {
    this.surveyEditor.affectedQuestionsOnConditionById(triggerQId).forEach((qId) => {
      const questionCtrl = this.surveyEditor.questionsCtrls.controls.find((q: FormGroup) => q.get('id').value === qId);
      this.removeConditionsByQuestionId(questionCtrl.get('visibleWhen') as FormArray, triggerQId);

      const options = questionCtrl.get('options') as FormArray;
      if (options?.value.length > 0) {
        options.controls.forEach((optCtrl) => this.removeConditionsByQuestionId(optCtrl.get('visibleWhen') as FormArray, triggerQId));
      }

      const cards = questionCtrl.get('cards') as FormArray;
      if (cards?.value.length > 0) {
        cards.controls.forEach((cardCtrl) => this.removeConditionsByQuestionId(cardCtrl.get('visibleWhen') as FormArray, triggerQId));
      }
    });
  }

  removeAffectedHiddenConditions(): void {
    const activeQuestionId = this.surveyEditor.activeQuestion.get('id').value;
    this.removeAffectedHiddenConditionsById(activeQuestionId);
  }

  removeAffectedHiddenConditionsById(triggerQId: string): void {
    this.surveyEditor.affectedHiddenQuestionsOnConditionById(triggerQId).forEach((qId) => {
      const questionCtrl = this.surveyEditor.questionsHiddenCtrls.controls.find((q: FormGroup) => q.get('id').value === qId);

      const options = questionCtrl.get('options') as FormArray;
      if (options?.value.length > 0) {
        options.controls.forEach((optCtrl) => this.removeConditionsByQuestionId(optCtrl.get('selectedWhen') as FormArray, triggerQId));
      }
    });
  }

  removeAffectedBlockConditions(): void {
    const activeQuestionId = this.surveyEditor.activeQuestion.get('id').value;

    this.surveyEditor.blocks.affectedConditions(activeQuestionId).forEach((blockId: string) => {
      const visibleWhen = this.surveyEditor.blocks.all.controls.find((block) => block.get('id').value === blockId).controls.visibleWhen;
      this.removeConditionsByQuestionId(visibleWhen, activeQuestionId);
    });
  }

  removeConditionsByQuestionId(visibleWhen: FormArray, qId: string): void {
    let indexToRemove = visibleWhen.value.findIndex((vw: Condition) => vw.questionId === qId);

    do {
      this.removeConditionFromArray(visibleWhen, indexToRemove);
      indexToRemove = visibleWhen.value.findIndex((vw: Condition) => vw.questionId === qId);
    } while (indexToRemove > -1);
  }

  removeIncompatibleConditionsById(triggerQId: string, allowedOperators: Operator[]): void {
    const isIncompatible = (vw: Condition): boolean => vw.questionId === triggerQId && !allowedOperators.includes(vw.operator);

    this.surveyEditor.affectedQuestionsOnConditionById(triggerQId).forEach((qId) => {
      const questionCtrl = this.surveyEditor.questionsCtrls.controls.find((q: FormGroup) => q.get('id').value === qId);
      this.removeConditionsWhere(questionCtrl.get('visibleWhen') as FormArray, isIncompatible);

      const options = questionCtrl.get('options') as FormArray;
      if (options?.value.length > 0) {
        options.controls.forEach((optCtrl) => this.removeConditionsWhere(optCtrl.get('visibleWhen') as FormArray, isIncompatible));
      }

      const cards = questionCtrl.get('cards') as FormArray;
      if (cards?.value.length > 0) {
        cards.controls.forEach((cardCtrl) => this.removeConditionsWhere(cardCtrl.get('visibleWhen') as FormArray, isIncompatible));
      }
    });

    this.surveyEditor.blocks.affectedConditions(triggerQId).forEach((blockId) => {
      const visibleWhen = this.surveyEditor.blocks.all.controls.find((block) => block.get('id').value === blockId).controls.visibleWhen;
      this.removeConditionsWhere(visibleWhen, isIncompatible);
    });

    this.surveyEditor.affectedHiddenQuestionsOnConditionById(triggerQId).forEach((qId) => {
      const questionCtrl = this.surveyEditor.questionsHiddenCtrls.controls.find((q: FormGroup) => q.get('id').value === qId);
      const options = questionCtrl.get('options') as FormArray;
      if (options?.value.length > 0) {
        options.controls.forEach((optCtrl) => this.removeConditionsWhere(optCtrl.get('selectedWhen') as FormArray, isIncompatible));
      }
    });
  }

  private affectedConditionalsOnQuestions(previousIndex: number): string[] {
    const affectedConditionsOnQuestion = this.surveyEditor.questionsCtrls.controls[previousIndex]
      .get('visibleWhen')
      .value.map((vw: Condition) => vw.questionId);

    let affectedConditionsOnQuestionOptions = [];
    if (this.surveyEditor.questions[previousIndex].options?.length > 0) {
      affectedConditionsOnQuestionOptions = this.surveyEditor.questions[previousIndex].options.reduce((conditionals, opt) => {
        const conditionQuestions = opt.visibleWhen.map((vw: Condition) => vw.questionId);
        if (conditionQuestions.length > 0) {
          return [...conditionals, ...conditionQuestions];
        } else {
          return conditionals;
        }
      }, []);
    }

    let affectedConditionsOnQuestionCards = [];
    if (this.surveyEditor.questions[previousIndex].cards?.length > 0) {
      affectedConditionsOnQuestionCards = this.surveyEditor.questions[previousIndex].cards.reduce((conditionals, card) => {
        const conditionQuestions = card.visibleWhen.map((vw: Condition) => vw.questionId);
        if (conditionQuestions.length > 0) {
          return [...conditionals, ...conditionQuestions];
        } else {
          return conditionals;
        }
      }, []);
    }

    return [...affectedConditionsOnQuestion, ...affectedConditionsOnQuestionOptions, ...affectedConditionsOnQuestionCards];
  }

  private affectedBlockConditionalsByMove(qMovingId: string, currentIndex: number): { blockId: string; questionId: string }[] {
    // TODO (When doing move task): Check this logic so conditions are always kept making sense for sequential blocks
    const blocksAffected: { blockId: string; questionId: string }[] = [];
    const groupBlock = this.surveyEditor.blocks.byGroupedQId(qMovingId);

    if (groupBlock) {
      const affectedConditionsOnBlock = groupBlock.visibleWhen.map((vw: Condition) => vw.questionId);

      affectedConditionsOnBlock.forEach((affectedQuestionId) => {
        const questionIndex = this.surveyEditor.getQuestionIndex(affectedQuestionId);
        if (questionIndex >= currentIndex) {
          blocksAffected.push({ blockId: groupBlock.id, questionId: affectedQuestionId });
        }
      });
    }

    const groupBlocks = this.surveyEditor.blocks.affectedConditions(qMovingId);
    if (!groupBlocks.length) {
      return blocksAffected;
    }

    // Removing the trigger shifts child positions; compare both identities in the prospective order.
    const questionsAfterMove = [...this.surveyEditor.questions];
    const previousIndex = questionsAfterMove.findIndex((question) => question.id === qMovingId);
    const [movedQuestion] = questionsAfterMove.splice(previousIndex, 1);
    questionsAfterMove.splice(currentIndex, 0, movedQuestion);
    const movedQuestionIndex = questionsAfterMove.findIndex((question) => question.id === qMovingId);

    for (const blockId of groupBlocks) {
      const groupBlockAffected = this.surveyEditor.blocks.byId(blockId);
      if (BlockTypeContainers.includes(groupBlockAffected.type) && groupBlockAffected.groupedBlockIds.length > 0) {
        const parentStartIndex = this.surveyEditor.blocks.parentStartQuestionIndex(groupBlockAffected.groupedBlockIds, questionsAfterMove);
        if (parentStartIndex !== -1 && movedQuestionIndex >= parentStartIndex) {
          blocksAffected.push({ blockId, questionId: qMovingId });
        }
        continue;
      }

      groupBlockAffected.groupedQuestionIds.forEach((affectedQuestionId) => {
        const questionIndex = this.surveyEditor.getQuestionIndex(affectedQuestionId);
        if (questionIndex >= currentIndex) {
          blocksAffected.push({ blockId: groupBlockAffected.id, questionId: qMovingId });
        }
      });
    }

    return blocksAffected;
  }

  private canApplyCondition(): boolean {
    if (
      !this.questionSelected$() ||
      !this.conditionFormValues$().operator ||
      (this.questionSelected$().cards?.length > 0 && !this.conditionFormValues$().cardId) ||
      (this.questionSelected$().type === QuestionType.Ranking && !this.conditionFormValues$().optionId)
    ) {
      return false;
    }

    if (
      BooleanOperators.includes(this.conditionFormValues$().operator) ||
      (NumberOperators.includes(this.conditionFormValues$().operator) && !!this.conditionFormValues$().valueToMatch)
    ) {
      return true;
    }

    return this.conditionFormValues$().valueToMatch?.length > 0;
  }

  private findConditionIndexAndRemove(
    conditionals: FormArray,
    questionIdsToRemoveOnConditions: string[],
    optionId: string = null,
    cardId: string = null
  ): void {
    questionIdsToRemoveOnConditions.forEach((qId) => {
      const conditionIndex = conditionals.value.findIndex(
        (vw: Condition) => vw.questionId === qId && (!optionId || vw.valueToMatch.includes(optionId)) && (!cardId || vw.cardId === cardId)
      );
      this.removeConditionFromArray(conditionals, conditionIndex);
    });
  }

  private removeConditionFromArray(conditionalsArray: FormArray, conditionIndex: number): void {
    if (conditionIndex > 0) {
      conditionalsArray.removeAt(conditionIndex);
      conditionalsArray.removeAt(conditionIndex - 1);
    } else {
      conditionalsArray.removeAt(conditionIndex);
      conditionalsArray.removeAt(conditionIndex);
    }
  }

  private removeConditionsWhere(visibleWhen: FormArray, predicate: (condition: Condition) => boolean): void {
    let indexToRemove = visibleWhen.value.findIndex(predicate);

    while (indexToRemove > -1) {
      this.removeConditionFromArray(visibleWhen, indexToRemove);
      indexToRemove = visibleWhen.value.findIndex(predicate);
    }
  }

  private handleGroupQuestionsChange(data: { blocks: BlockModel[]; parentBlock?: BlockModel }): void {
    if (!!data.parentBlock) {
      const hasQuestions = data.blocks.some((block) => block.groupedQuestionIds.length > 0);
      if (!hasQuestions) {
        this.clearBlockConditions(data.parentBlock.id);
      }
      return;
    }

    this.clearBlockConditions(data.blocks[0].id);
  }

  private clearBlockConditions(blockId: string): void {
    const blockConditionals = this.surveyEditor.blocks.all.controls.find((c) => c.value.id === blockId).get('visibleWhen') as FormArray;
    if (!blockConditionals || blockConditionals.length === 0) {
      return;
    }

    blockConditionals.clear();

    this.snackBar.open(`The conditions set on block ${blockId} have been removed`, 'Ok', {
      duration: 3000,
      panelClass: SnackbarMode.Alert
    });
  }
}
